/**
 * Les icônes de la coquille — **le tracé de la maquette**, pas celui
 * d'une bibliothèque tierce.
 *
 * Les commandes de tracé sont recopiées de la carte `icons` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`,
 * dans la boîte à 24 × 24 où elles y sont dessinées. Prendre les icônes d'un
 * jeu tiers « qui y ressemble » aurait donné un dessin voisin et faux : la
 * maquette est le contrat, et une icône qui n'a pas le même trait est une
 * divergence qu'aucun contrôle ne verrait.
 *
 * Deux tests confrontent cette table à la maquette elle-même, tracé par tracé :
 * `test/app/sidebar-maquette.test.tsx` pour les seize icônes de modules,
 * `test/app/topbar-maquette.test.tsx` pour les deux de la barre supérieure.
 * Recopier de travers fait donc rougir.
 *
 * **Deux icônes ne désignent pas un module** — `search` et `download` sont
 * celles de la barre supérieure. Elles vivent ici quand même : c'est la table
 * des tracés de la maquette, et en ouvrir une seconde à côté ferait deux
 * endroits où recopier de travers.
 *
 * **Aucune mesure ici.** Taille, épaisseur de trait et couleur viennent de la
 * feuille de style — `.shell__nav-icon` ou `.shell__topbar-icon` : une largeur
 * écrite dans le JSX serait une valeur de design de plus à tenir à jour, et
 * `design-token-check.ts` refuse déjà ce genre de doublon.
 */

import type { ReactNode } from 'react';

/**
 * Un élément de dessin : une commande de chemin SVG, ou un cercle
 * `[cx, cy, r]`.
 *
 * La maquette emploie les deux formes — `lifeBuoy` est fait de deux cercles et
 * de quatre segments. Ne garder que les chemins aurait amputé l'icône du
 * support de tout ce qui la rend reconnaissable.
 */
export type IconShape = string | readonly [number, number, number];

/**
 * Les tracés, tels que la maquette les dessine, dans une boîte de 24 × 24.
 *
 * **Une exception, et elle est nommée** : `eye` n'est PAS de la maquette. Le
 * dessin ne porte aucune colonne d'action, et le porteur en a demandé une —
 * « ajoute d'autres éléments sur la table » — pour que l'affordance de la rangée
 * cliquable se voie. Elle est écrite dans la même boîte et le même langage de
 * trait que les autres, et elle est signalée ici pour qu'on ne la cherche pas
 * dans le fichier `.dc.html` : personne ne l'y trouvera, et ce n'est pas une
 * recopie de travers.
 */
export const iconShapes = {
  chart: ['M3 3v18h18', 'M7 16v-5', 'M12 16V8', 'M17 16v-9'],
  download: ['M12 3v12', 'm7 12 5 5 5-5', 'M4 21h16'],
  // Voir — la seule icône de cette table que la maquette ne dessine pas. Voir
  // le commentaire de tête : c'est une demande du porteur, pas un oubli de
  // recopie.
  eye: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z', [12, 12, 3]],
  fileText: [
    'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
    'M14 2v6h6',
    'M8 13h8',
    'M8 17h8',
  ],
  grid: ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M3 14h7v7H3z', 'M14 14h7v7h-7z'],
  idCard: ['M3 5h18v14H3z', 'M7 15h4', 'M7 11h.01', 'M15 9h2', 'M15 13h2'],
  landmark: ['M3 22h18', 'M6 18v-7', 'M10 18v-7', 'M14 18v-7', 'M18 18v-7', 'm12 2 9 6H3z'],
  lifeBuoy: [
    [12, 12, 10],
    [12, 12, 4],
    'm4.9 4.9 3.2 3.2',
    'm15.9 15.9 3.2 3.2',
    'm19.1 4.9-3.2 3.2',
    'm8.1 15.9-3.2 3.2',
  ],
  search: [[11, 11, 8], 'm21 21-4.3-4.3'],
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  sliders: [
    'M4 21v-7',
    'M4 10V3',
    'M12 21v-9',
    'M12 8V3',
    'M20 21v-5',
    'M20 12V3',
    'M1 14h6',
    'M9 8h6',
    'M17 16h6',
  ],
  store: [
    'm2 7 2-4h16l2 4',
    'M4 7v13a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7',
    'M4 7h16',
    'M9 21v-6h6v6',
  ],
  swap: ['m7 4 3 3-3 3', 'M10 7H3', 'm17 20-3-3 3-3', 'M14 17h7'],
  tag: [
    'm20.6 12.6-8 8a2 2 0 0 1-2.8 0L2.4 13.2A2 2 0 0 1 2 11.8V4a2 2 0 0 1 2-2h7.8a2 2 0 0 1 1.4.6l7.4 7.4a2 2 0 0 1 0 2.6z',
    'M7 7h.01',
  ],
  users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', [9, 7, 4], 'M22 21v-2a4 4 0 0 0-3-3.87'],
  wallet: ['M3 7h18v12H3z', 'M3 7V5h13v2', 'M16 13h2'],
} as const satisfies Readonly<Record<string, readonly IconShape[]>>;

/** Le nom d'une icône de la maquette. */
export type IconName = keyof typeof iconShapes;

/**
 * Une icône de la coquille.
 *
 * `aria-hidden` : l'icône double le libellé, elle ne le remplace pas. Annoncée,
 * elle ferait dire deux fois la même chose à un lecteur d'écran.
 *
 * [className] désigne la règle qui porte la **taille** et le trait — la barre
 * latérale dessine à 15 points, la barre supérieure à 14. Aucune des deux n'est
 * écrite ici : une largeur posée dans le JSX serait une valeur de design de
 * plus à tenir à jour.
 */
export function ModuleIcon({
  name,
  className = 'shell__nav-icon',
}: {
  readonly name: IconName;
  readonly className?: string;
}): ReactNode {
  return (
    <svg aria-hidden="true" className={className} focusable="false" viewBox="0 0 24 24">
      {iconShapes[name].map((forme) =>
        typeof forme === 'string' ? (
          <path d={forme} key={forme} />
        ) : (
          <circle
            cx={forme[0]}
            cy={forme[1]}
            key={`${forme[0]}·${forme[1]}·${forme[2]}`}
            r={forme[2]}
          />
        ),
      )}
    </svg>
  );
}
